import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Autocomplete,
  TextField,
  CircularProgress,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchRealEstateAddress,
  setSelectedRealEstateAddress,
  setRealEstatePresetsSelectedRow,
  setSelectedRow,
  fillCommonValues,
} from "../../store/actions/uiActions/myPermitsActions";
import {
  setRealEstatePresetsPermitPackage,
  updatePermitPackage,
  setQuickActionAddress,
} from "../../store/actions/dataActions";
import { getFormInitials } from "../../store/actions/permitFormActions/buildingStateActions";
import { normalizeStreetName } from "../../utils/suffix";
import axios from "axios";

// Counties that support property database fetching
const PROPERTY_DATABASE_COUNTIES = [
  "duval",
  "st_johns",
  "st johns",
  "clay",
  "polk",
];

const PropertyAddressFetchModal = ({ open, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const { selectedRow } = useSelector((state) => state.myPermitsComponent);
  const realEstateSet = selectedRow?.realEstatePresets?.realEstateSet;
  const selectedPreset = selectedRow?.realEstatePresets?.selectedAddress;
  const { addressOptions, fetchLoading, selectedAddressTemp } = useSelector(
    (state) => state.myPermitsComponent.realEstate,
  );

  const { address: tempAddress } = useSelector(
    (state) => state.nocData.quickActions,
  );

  const [inputValue, setInputValue] = useState("");
  const [selectedOption, setSelectedOption] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);

  const debounceTimerRef = useRef(null);
  const lastFetchedAddressRef = useRef("");
  const suppressFetchRef = useRef(false);
  const tempAddressRef = useRef(null);

  // Format address for display
  const formatAddressOption = useCallback((option) => {
    if (!option || typeof option === "string") return option || "";
    const { city, zip_code, full_address } = option;

    return `${full_address}, ${city}, ${zip_code}`.trim();
  }, []);

  // Fetch addresses when user types
  useEffect(() => {
    // Only fetch if modal is open
    if (!open) return;

    if (window.__suppressRealEstateFetch) {
      window.__suppressRealEstateFetch = false;
      return;
    }

    if (inputValue.trim() === lastFetchedAddressRef.current) return;
    lastFetchedAddressRef.current = inputValue.trim();

    if (suppressFetchRef.current) {
      suppressFetchRef.current = false;
      return;
    }

    if (!inputValue || typeof inputValue !== "string") return;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      dispatch(fetchRealEstateAddress(inputValue.trim()));
    }, 1000);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [inputValue, dispatch, open]);

  // Prefill address from quickActions (dashboard quick actions)
  useEffect(() => {
    if (!open) return; // Only run when modal is open

    const handleFetchRealEstate = async () => {
      if (!tempAddress?.fullStreetAddress) return;

      const normalizedAddress = normalizeStreetName(
        tempAddress.fullStreetAddress,
      );
      const options = await dispatch(fetchRealEstateAddress(normalizedAddress));

      if (!Array.isArray(options) || options.length === 0) {
        await dispatch(
          setQuickActionAddress({
            fullStreetAddress: "",
            houseNumber: "",
            streetName: "",
            parcelID: "",
          }),
        );
        return;
      }

      const formattedInput = tempAddress.fullStreetAddress.toUpperCase();
      // Suppress the manual input fetch to prevent infinite loop
      suppressFetchRef.current = true;
      setInputValue(formattedInput);

      if (options.length === 1) {
        // Simulate user selecting it
        const option = options[0];
        setSelectedOption(option);
        dispatch(setSelectedRealEstateAddress(option));
      } else {
        // Multiple options - user can select from dropdown
        // The options are already in addressOptions from the fetch
      }

      // Clean up temp quickAction so it doesn't repeat
      await dispatch(
        setQuickActionAddress({
          fullStreetAddress: "",
          houseNumber: "",
          streetName: "",
        }),
      );
    };

    // Run if tempAddress exists and we haven't processed it yet for this modal session
    if (tempAddress?.fullStreetAddress && !tempAddressRef?.current) {
      tempAddressRef.current = true;
      handleFetchRealEstate();
    }
  }, [tempAddress?.fullStreetAddress, dispatch, open]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setInputValue("");
      setSelectedOption(null);
      setSaveLoading(false);
      lastFetchedAddressRef.current = "";
      tempAddressRef.current = null; // Reset ref so it can run again next time
    }
  }, [open]);

  // Handle address selection
  const handleAddressChange = (e, value) => {
    suppressFetchRef.current = true;
    setSelectedOption(value);
    setInputValue(value ? formatAddressOption(value) : "");
    dispatch(setSelectedRealEstateAddress(value));
  };

  // Save property data to permit package
  const handleSavePropertyData = async () => {
    if (!selectedOption) return;

    try {
      setSaveLoading(true);

      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/real-estate/preset/${selectedRow?.county.toLowerCase().replace(" ", "_")}`,
        {
          currentSession: currentSession,
          task_id: selectedRow?.task_id,
          selected_address: selectedOption,
          county: selectedRow?.county,
          form_field_obj: {},
          skip_noc: true, // Flag to indicate this is not for NOC creation
        },
        {
          withCredentials: true,
        },
      );

      if (res?.data?.success) {
        await dispatch(
          setRealEstatePresetsSelectedRow({
            data: res?.data?.data,
            address: selectedOption,
          }),
        );
        await dispatch(
          setRealEstatePresetsPermitPackage({
            realEstatePresets: res?.data?.data,
            task_id: selectedRow?.task_id,
          }),
        );

        // Backend now returns the fully updated permit package with common_fields, property_owner, and project_address
        const updatedPermitPackage = res?.data?.data;

        // Use the backend's updated data directly
        const updatedSelectedRow = {
          ...selectedRow,
          ...updatedPermitPackage,
        };

        // Update the row in the grid/list (permitPackages array)
        await dispatch(updatePermitPackage(updatedSelectedRow));

        // Update the selectedRow in Redux
        await dispatch(setSelectedRow(updatedSelectedRow));

        // Trigger form fields to refill with new common_fields values
        await dispatch(fillCommonValues());
        // Refresh initials so ComplexPermitForm merge picks up updated common values
        await dispatch(getFormInitials());

        // Force re-mount of the form so controls rebind to updated values
        await dispatch({ type: "BUMP_FORM_BIND_VERSION" });

        // Call onSuccess callback
        if (onSuccess) {
          onSuccess(res?.data?.data);
        }

        // Close modal
        onClose();
      }
    } catch (error) {
      console.error("Error saving property data:", error);
    } finally {
      setSaveLoading(false);
    }
  };

  // Handle skip
  const handleSkip = () => {
    onClose();
  };

  // Check if county supports property database - only show modal for duval, st_johns, clay, polk
  const county = selectedRow?.county?.toLowerCase()?.replace(/\s+/g, "_");
  const isSupportedCounty = PROPERTY_DATABASE_COUNTIES.includes(county);

  // Don't render modal if county is not supported
  if (!isSupportedCounty) {
    return null;
  }

  return (
    <Dialog open={open} onClose={() => {}} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: "bold", color: "primary.main" }}>
        Fetch Property Details
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Would you like to fetch property details to auto-populate your
            permit forms? This will retrieve owner information, property
            address, and other relevant details from the{" "}
            <strong>
              {selectedRow?.county === "Duval"
                ? "Duval County Property Appraiser"
                : selectedRow?.county === "St Johns" ||
                    selectedRow?.county === "St_Johns"
                  ? "St. Johns County Property Appraiser"
                  : selectedRow?.county === "Polk"
                    ? "Polk County Property Appraiser"
                    : "Clay County Property Appraiser"}
            </strong>{" "}
            database.
          </Typography>

          <Autocomplete
            options={addressOptions || []}
            loading={fetchLoading}
            value={
              realEstateSet
                ? selectedPreset
                : selectedAddressTemp
                  ? selectedAddressTemp
                  : selectedOption
            }
            onChange={handleAddressChange}
            inputValue={
              inputValue !== ""
                ? inputValue
                : realEstateSet
                  ? formatAddressOption(selectedPreset)
                  : selectedAddressTemp
                    ? formatAddressOption(selectedAddressTemp)
                    : ""
            }
            onInputChange={(e, newInputValue) => {
              setInputValue(newInputValue);
            }}
            getOptionLabel={(option) => formatAddressOption(option)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search Property Address"
                placeholder="Start typing an address..."
                variant="filled"
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {fetchLoading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                  inputLabel: {
                    shrink: true,
                  },
                }}
              />
            )}
            renderOption={(props, option, { index }) => (
              <li {...props} key={`${option.parcel_number}-${index}`}>
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    {formatAddressOption(option)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Owner: {option.owner_name || "N/A"} | Parcel:{" "}
                    {option.parcel_number || option.parcel_id || "N/A"}
                  </Typography>
                </Box>
              </li>
            )}
            noOptionsText={
              inputValue.trim()
                ? "No properties found. Try a different address."
                : "Start typing to search for properties..."
            }
          />

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: "block" }}
          >
            Tip: Enter a street address like "123 Main St" to search for
            properties
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleSkip} disabled={saveLoading}>
          Skip for Now
        </Button>
        <LoadingButton
          onClick={handleSavePropertyData}
          loading={saveLoading}
          disabled={!selectedOption}
          variant="contained"
          color="primary"
        >
          Fetch & Apply Property Details
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(PropertyAddressFetchModal);
